'use client';

import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../utils/constants';
import auth from '../../Auth';
import { Loader2, Settings, CircleChevronUp, Pencil, Plus, MoveUp, MoveDown } from 'lucide-react';
import Breadcrumbs from '../Breadcrumbs';
import { Button } from '@nextui-org/react';
import { apiFetchModels } from '../../utils/api';
import { Play, Trash2, RotateCw, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import ModelComparisonChart from './ModelComparisonChart';
import { TOAST_STYLE_ERROR, TOAST_STYLE_SUCCESS } from '../../utils/constants';
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Input, Select, SelectItem } from '@nextui-org/react';

const PromptTests = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const timestampParam = query.get('timestamp', '');
  const versionParam = query.get('version', '');
  const { prompt_id } = useParams();
  const [logTimestamps, setLogTimestamps] = useState([]);
  const [promptVersion, setVersion] = useState(versionParam);
  const [modelName, setModelName] = useState('');
  const [timestamp, setTimestamp] = useState(timestampParam);
  const [models, setModels] = useState([]);
  const [isFetchedTests, setIsFetchedTests] = useState(false);
  const [testContext, setTestContext] = useState('{}');
  const [promptTests, setPromptTests] = useState([]);
  const [promptVersions, setPromptVersions] = useState([]);
  const [isFetchedVersions, setIsFetchedVersions] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [newModel, setNewModel] = useState('');
  const [isUpdatingModel, setIsUpdatingModel] = useState(false);
  const [selectedModelFilter, setSelectedModelFilter] = useState('');
  const [fetchedModels, setFetchedModels] = useState(false);
  const [showVersionChart, setShowVersionChart] = useState(true);

  // 1. fetch all timestamps available
  const fetchTimestamps = async () => {
    const response = await axios.get(`${API_URL}/logs/timestamps?prompt_id=${prompt_id}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });

    if (response.status === 200) {
      const log_timestamps = response.data;
      setLogTimestamps(log_timestamps.timestamps);
      if (!timestamp) {
        let last = log_timestamps.timestamps.length - 1;
        if (last >= 0) {
          handleTimestampChange(log_timestamps.timestamps[last]);
        }
      }
    }
  };

  const fetchModels = async () => {
    try {
      const modelsData = await apiFetchModels();

      setModels(modelsData);
      if (modelsData.length === 0) {
        setSelectedModelFilter('');
      } else {
        setSelectedModelFilter(modelsData[0]);
      }
      fetchPromptWithVersion(modelsData.length === 0, true);
      // fetchBehaviorName(behaviorsData.length === 0)
    } catch (error) {
      console.error(error.message || 'Error fetching models');
    }
  };

  const fetchPromptTests = async () => {
    await axios
      .get(`${API_URL}/tests?prompt_id=${prompt_id}&getTests`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`
        }
      })
      .then((res) => {
        setPromptTests(res.data);
        setIsFetchedTests(true);
      })
      .catch((err) => console.error(err));
  };

  const fetchPromptVersions = async () => {
    try {
      const response = await axios.get(`${API_URL}/prompts/${prompt_id}/versions`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`
        }
      });
      if (response.status === 200) {
        setPromptVersions(response.data);
        response.data.forEach((item) => {
          if (item.latest) {
            setLatestVersion(item.version);
            setSelectedVersion(item.version);
          }
        });
        setIsFetchedVersions(true);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const fetchPromptWithVersion = async (isZero, process) => {
    var get_params = '';
    if (promptVersion) {
      get_params = `?version=${promptVersion}`;
    }
    const response = await axios.get(`${API_URL}/prompts/${prompt_id}${get_params}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });
    if (response.status === 200) {
      const fetchedPrompt = response.data;
      setVersion(fetchedPrompt.version);

      if (process) {
        if (fetchedPrompt.model_name !== '') {
          setModelName(fetchedPrompt.model_name);
          setSelectedModelFilter(fetchedPrompt.model_name);
        } else if (isZero) {
          setModelName('');
          setSelectedModelFilter('gpt-4o-mini-system');
        }
        setFetchedModels(true);
      }
    }
  };

  const fetchPromptWithTimestamp = async () => {
    const response = await axios.get(`${API_URL}/logs/log?prompt_id=${prompt_id}&timestamp=${timestamp}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });
    if (response.status === 200) {
      const log_response = response.data;
      console.log('log_response', log_response);
      setTimestamp(log_response.timestamp);
    }
  };

  // Adjusted fetchContext to accept newTimestamp parameter
  const fetchContext = async (newTimestamp = timestamp) => {
    let ts_part = '';
    if (newTimestamp) {
      ts_part = `&timestamp=${newTimestamp}`;
    }
    // TODO: What is that?
    const response = await axios.get(`${API_URL}/logs/context?prompt_id=${prompt_id}${ts_part}`, {
      headers: {
        Authorization: `Bearer ${await auth.getIdToken()}`
      }
    });
    if (response.status === 200) {
      setTestContext(JSON.stringify(response.data, null, 2));
    }
  };
  const handleTimestampChange = async (newTimestamp) => {
    if (newTimestamp !== undefined) {
      setTimestamp(newTimestamp);
      await fetchContext(newTimestamp);
    }
  };
  // 2. fetch prompt data with version or timestamp from logs
  useEffect(() => {
    const fetchLog = async () => {
      try {
        if (!timestamp) {
          fetchPromptWithVersion(true, false);
        } else {
          fetchPromptWithTimestamp();
        }
        fetchTimestamps();
      } catch (error) {
        console.error('Error fetching prompt:', error);
      }
    };
    fetchModels();
    fetchLog();
    fetchPromptTests();
    fetchPromptVersions();

    const intervalId = setInterval(() => {
      fetchPromptTests();
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: `/tests`, breadcrumb: 'CI/CD' },
    { path: window.location.href, breadcrumb: `${prompt_id}` }
  ];

  const [selectedVersion, setSelectedVersion] = useState('');

  // Handler for version change in the dropdown
  const handleVersionChange = (version) => {
    setSelectedVersion(version);
  };

  const handleModelFilterChange = (model) => {
    setSelectedModelFilter(model);
  };

  const handleRunTestVersion = async (rowId) => {
    if (selectedVersion) {
      await axios
        .post(
          `${API_URL}/tests?runVersion`,
          {
            test_id: rowId,
            prompt_id,
            prompt_version: selectedVersion,
            model_name: selectedModelFilter,
            test_context: JSON.parse(testContext)
          },
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then((res) => {
          console.log('Test started successfuly!');
          toast.success('Test started successfuly', TOAST_STYLE_SUCCESS);
        })
        .catch((err) => console.error(err));
    } else {
      toast.error('Please selected a version!', TOAST_STYLE_ERROR);
    }
  };

  const handleDeleteTest = async (testId) => {
    await axios
      .delete(`${API_URL}/tests?deleteTest&test_id=${testId}&prompt_id=${prompt_id}`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      })
      .then(() => {
        toast.success(`Test is deleted!`, TOAST_STYLE_SUCCESS);
        fetchPromptTests();
      })
      .catch((err) => console.log(err));
  };

  const [preparingTests, setPreparingTests] = useState(false);
  const handleRunAllTestsVersion = async () => {
    if (selectedVersion) {
      toast.success('Preparing tests...', TOAST_STYLE_SUCCESS);
      setPreparingTests(true);
      await axios
        .post(
          `${API_URL}/tests?runAllVersion`,
          {
            test_ids: promptTests.map((item) => item.test_id),
            prompt_id,
            prompt_version: selectedVersion,
            model_name: selectedModelFilter
          },
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then((res) => {
          toast.success('Tests started successfuly!', TOAST_STYLE_SUCCESS);
          setPreparingTests(false);
        })
        .catch((err) => {
          toast.error('Tests run failed. Please rerun', TOAST_STYLE_ERROR);
          setPreparingTests(false);
          console.error(err);
        });
    } else {
      toast.error('Please selected a version!', TOAST_STYLE_ERROR);
    }
  };

  const handleUpdateModel = async () => {
    if (newModel !== '') {
      setIsUpdatingModel(true);
      await axios
        .put(
          `${API_URL}/tests?updatePromptModel`,
          {
            prompt_id,
            model_name: newModel
          },
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then(() => {
          fetchPromptWithVersion(models.length === 0, true);
          toast.success('Updated prompt model!', TOAST_STYLE_SUCCESS);
          setIsUpdatingModel(false);
          setSelectedModelFilter(newModel);
          setModelName(newModel);
        })
        .catch((err) => console.log(err));
    } else {
      toast.error('Please add secrets for models first!', TOAST_STYLE_ERROR);
    }
  };

  const handleImprovePrompt = async () => {
    await axios
      .post(
        `${API_URL}/tests?improvePrompt`,
        {
          prompt_id,
          prompt_version: promptVersion
        },
        {
          headers: {
            Authorization: `Bearer ${auth.syncGetIdToken()}`,
            'Content-Type': 'application/json'
          }
        }
      )
      .then((res) => {
        toast.success('Prompt is improving!', TOAST_STYLE_SUCCESS);
      })
      .catch((err) => console.error(err));
  };

  function getNestedAttribute(obj, nestedAttr, divider, appendix = '', defaultValue = '-', fallbackValue = '-') {
    // Construct the key for the average metrics
    const metricsKey = `${selectedModelFilter}_average_metrics`;

    // Check if the metricsKey exists in the object
    if (obj.hasOwnProperty(metricsKey)) {
      const metrics = obj[metricsKey];

      // Check if the version exists within the metrics
      if (metrics.hasOwnProperty(selectedVersion)) {
        const versionData = metrics[selectedVersion];

        // Check if the nested attribute exists within the version data
        if (versionData.hasOwnProperty(nestedAttr)) {
          if (nestedAttr === 'last_score' && versionData.hasOwnProperty('avg_score')) {
            if (Number(versionData['last_score']) < Number(versionData['avg_score'])) {
              return (
                <span style={{ color: 'red' }}>
                  {Math.round(versionData[nestedAttr] * divider) / divider}
                  {appendix}
                </span>
              );
            } else if (Number(versionData['last_score']) > Number(versionData['avg_score'])) {
              return (
                <span style={{ color: 'green' }}>
                  {Math.round(versionData[nestedAttr] * divider) / divider}
                  {appendix}
                </span>
              );
            }
          }

          return `${Math.round(versionData[nestedAttr] * divider) / divider}${appendix}`;
        }
        // Return fallback value if the nested attribute is not found
        return fallbackValue;
      }
    }

    // Return default value if metricsKey or version is not found
    return defaultValue;
  }

  function calculateAverages() {
    let totalScore = 0;
    let totalPrice = 0;
    let count = 0;

    promptTests.forEach((test) => {
      const metricsKey = `${selectedModelFilter}_average_metrics`;

      // Check if the behavior metrics and version exist in the current test
      if (test.hasOwnProperty(metricsKey) && test[metricsKey].hasOwnProperty(selectedVersion)) {
        const versionData = test[metricsKey][selectedVersion];

        // Ensure the versionData has both score and price
        if (versionData.hasOwnProperty('avg_score') && versionData.hasOwnProperty('last_price')) {
          totalScore += Number(versionData['avg_score']);
          totalPrice += Number(versionData['last_price']);
          count++;
        }
      }
    });

    // Calculate averages, avoid division by zero
    const averageScore = count > 0 ? `${Math.round((totalScore / count) * 100) / 100}%` : '-';
    const averagePrice = count > 0 ? `${Math.round((totalPrice / count) * 10000) / 10000}$` : '-';
    const price = count > 0 ? `(~${Math.round(totalPrice * 10000) / 10000}$)` : '';

    return {
      averageScore,
      averagePrice,
      totalPrice: price
    };
    }
  // Add a new state for chart visibility and type
  const [chartType, setChartType] = useState('model'); // 'none', 'version', 'model'
    
  // Use existing models array for available models
  const availableModels = models.length > 0 ? models : 
    modelName ? [modelName] : ['openai/gpt-4o-mini'];

  // Add "Compare Models" button near the existing model selector
  const renderModelComparisonButton = () => (
    <Button
      size="sm"
      color={chartType === 'model' ? "secondary" : "default"}
      onClick={() => setChartType(chartType === 'model' ? 'none' : 'model')}
      className="ml-2"
    >
      {chartType === 'model' ? 'Hide Comparison' : 'Compare Models'}
    </Button>
  );

  const getArrow = (test) => {
    const metricsKey = `${selectedModelFilter}_average_metrics`;
    if (test.hasOwnProperty(metricsKey) && test[metricsKey].hasOwnProperty(selectedVersion)) {
      const versionData = test[metricsKey][selectedVersion];
      if (versionData.hasOwnProperty('last_score') && versionData.hasOwnProperty('avg_score')) {
        const valA = Number(versionData['last_score']);
        const valB = Number(versionData['avg_score']);

        if (valA > valB) {
          return <MoveUp color='green' size={14} />;
        }

        if (valA < valB) {
          return <MoveDown color='red' size={14} />;
        }
      }
      return '';
    }
    return '';
  };

  return (
    <div className='flex flex-col w-full p-8 space-y-8'>
      <Breadcrumbs crumbs={crumbs} />
      
      {isFetchedTests && isFetchedVersions && fetchedModels && (
        <div className="flex justify-between items-center">
          <div className="text-xl font-bold">Tests for {prompt_id}</div>
          <div className="flex gap-2">
            <Button 
              size="sm"
              color={chartType === 'version' ? "secondary" : "default"}
              onClick={() => setChartType(chartType === 'version' ? 'none' : 'version')}
            >
              {chartType === 'version' ? 'Hide Version Chart' : 'Show Version Chart'}
            </Button>
            <Button 
              size="sm"
              color={chartType === 'model' ? "secondary" : "default"}
              onClick={() => setChartType(chartType === 'model' ? 'none' : 'model')}
            >
              {chartType === 'model' ? 'Hide Model Comparison' : 'Compare Models'}
            </Button>
          </div>
        </div>
      )}
      
      {chartType === 'model' && isFetchedTests && isFetchedVersions && fetchedModels && (
        <ModelComparisonChart 
          promptId={prompt_id}
          modelName={modelName}
          availableModels={availableModels}
          promptTests={promptTests}
          promptVersions={promptVersions}
        />
      )}
      
      <div className='w-full'>
        {isFetchedTests && isFetchedVersions && fetchedModels && (
          <>
            <div className='flex gap-4'>
              {promptTests.length > 0 && (
                <div className='flex gap-2 items-center mb-2 text-sm'>
                  <select
                    value={selectedVersion || ''}
                    onChange={(e) => handleVersionChange(e.target.value)}
                    className='mr-2 p-1 border rounded'
                  >
                    <option value={latestVersion}>Latest version</option>
                    {promptVersions.map((version) => !version.latest && <option value={version.version}>{version.version}</option>)}
                  </select>
                  <select
                    value={selectedModelFilter || ''}
                    onChange={(e) => handleModelFilterChange(e.target.value)}
                    className='mr-2 p-1 border rounded'
                  >
                    {modelName === '' && models.length === 0 && <option value='openai/gpt-4o-mini'>lamoom--openai/gpt-4o-mini</option>}
                    {modelName !== '' && <option value={modelName}>{modelName}</option>}
                    {models.map(
                      (model) =>
                        model !== modelName && <option value={model}>{model}</option>
                    )}
                  </select>
                  {/* Add the "Compare Models" button next to the model dropdown */}
                  {renderModelComparisonButton()}
                  <Settings
                    cursor='pointer'
                    color='gray'
                    onClick={() => {
                      setShowSettings((prev) => !prev);
                      if (models.length !== 0) {
                        setNewModel(models[0]);
                      }
                    }}
                  ></Settings>
                </div>
              )}
              {showSettings && (
                <div>
                  <span className='font-semibold'>Change prompt model</span>
                  <div className='flex items-center'>
                    <select
                      value={newModel || ''}
                      onChange={(e) => setNewModel(e.target.value)}
                      className='mr-2 p-1 border rounded text-sm'
                    >
                      {models.map((model) => (
                        <option value={model}>{model}</option>
                      ))}
                    </select>
                    {!isUpdatingModel ? (
                      <div className='relative group'>
                        <button className='px-2 py-2 bg-gray-100 text-white rounded-lg text-sm' onClick={handleUpdateModel}>
                          <Check color='black' size={16} />
                        </button>
                        <span className='absolute left-1/2 transform -translate-x-1/2 bottom-full mb-1 hidden group-hover:block bg-gray-200 text-black text-xs rounded-lg px-2 py-1 whitespace-nowrap'>
                          Apply
                        </span>
                      </div>
                    ) : (
                      <Loader2 size={18} className='animate-spin' />
                    )}
                  </div>
                </div>
              )}
            </div>
            <Table className='mt-2 w-full max-h-[65vh]' isHeaderSticky aria-label='Prompt Library Table'>
              <TableHeader>
                <TableColumn />
                <TableColumn>Test name</TableColumn>
                <TableColumn>Last Score</TableColumn>
                <TableColumn>Avg Score ({calculateAverages().averageScore})</TableColumn>
                <TableColumn>Last Run Price ({calculateAverages().averagePrice})</TableColumn>
                <TableColumn>Created</TableColumn>
                <TableColumn>
                  <div className='flex gap-2 items-center'>
                    {preparingTests && !fetchedModels ? (
                      <Loader2 color='black' size={17} className='animate-spin' />
                    ) : (
                      promptTests.length !== 0 && (
                        <div className='relative group'>
                          <button onClick={() => handleRunAllTestsVersion()} className='px-2 py-2 bg-gray-100 text-white rounded-lg'>
                            <Play color='black' size={17} />
                          </button>
                          <span className='absolute transform bottom-full mb-[-6px] hidden group-hover:block bg-gray-200 text-black text-xs rounded-lg px-2 py-1 whitespace-nowrap'>
                            Run all tests
                          </span>
                        </div>
                      )
                    )}
                    <span>{calculateAverages().totalPrice}</span>
                  </div>
                </TableColumn>
              </TableHeader>
              <TableBody>
                {promptTests.map((test, idx) => (
                  <TableRow key={test.test_id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell
                      className='cursor-pointer text-blue-500 hover:bg-gray-100'
                      onClick={() => {
                        window.open(`/tests/${prompt_id}/${test.test_id}`);
                      }}
                    >
                      {test.name}
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center'>
                        {getNestedAttribute(test, 'last_score', 100, '%')} {getArrow(test)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className='font-bold'>{getNestedAttribute(test, 'avg_score', 100, '%')}</span>
                    </TableCell>
                    <TableCell>{getNestedAttribute(test, 'last_price', 10000, '$')}</TableCell>
                    <TableCell>{test.created_dtf}</TableCell>
                    <TableCell>
                      <div className='flex gap-2'>
                        <button onClick={() => handleRunTestVersion(test.test_id)} className='px-4 py-2 bg-gray-100 text-white rounded-lg'>
                          <Play color='gray' size={15} />
                        </button>
                        <button
                          onClick={() => handleDeleteTest(test.test_id, test.name)}
                          className='px-4 py-2 bg-gray-100 text-white rounded-lg'
                        >
                          <Trash2 color='gray' size={15} />
                        </button>
                        <button
                          onClick={() => window.open(`/tests/edit/${prompt_id}/${test.test_id}`)}
                          className='px-4 py-2 bg-gray-100 text-white rounded-lg'
                        >
                          <Pencil color='gray' size={15} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {!isFetchedTests && <Loader2 className='m-auto mt-3 animate-spin' />}
      </div>

      {isFetchedTests && isFetchedVersions && fetchedModels && (
        <Button color='primary' className='success w-fit' onClick={() => window.open(`/tests/${prompt_id}/new`)}>
          <Plus />
        </Button>
      )}
    </div>
  );
};

export default PromptTests;
